import React, { useEffect, useRef, useState } from 'react'
import { usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import GLightbox from 'glightbox'
import 'glightbox/dist/css/glightbox.min.css'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import 'react-datepicker/dist/react-datepicker.css'
import AlertDismissible from '../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../Shared/Components/Breadcrumbs'
import PaginationList from '../../Shared/Components/PaginationList'
import Layout from '../../Shared/Layout/LayoutAdmin'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import SectionBadge from './SectionBadge'
import {
    formatAnnouncementDate,
    formatAnnouncementDateTime,
    fromYmd,
    toYmd,
} from '../../utils/announcementsPermissions'
import './announcements.css'

registerLocale('es', es)

const cleanFilters = (filters) =>
    Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '' && value !== null && value !== undefined))

function Reader({ announcement, onBack }) {
    if (!announcement) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center text-center text-gray-500 py-20 px-6 h-100">
                <i className="bi bi-envelope-open fs-3x mb-4 text-gray-400"></i>
                <div className="fs-5">Selecciona un comunicado para leerlo.</div>
            </div>
        )
    }

    const openImage = (index) => {
        GLightbox({
            openEffect: 'fade',
            closeEffect: 'fade',
            elements: announcement.images.map((img) => ({ href: img.url, type: 'image' })),
        }).openAt(index)
    }

    return (
        <div className="announcements-reader px-6 px-lg-8 py-6">
            <Button variant="light" size="sm" className="d-lg-none mb-5" onClick={onBack}>
                <i className="bi bi-arrow-left me-1"></i> Volver a la lista
            </Button>
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                <SectionBadge section={announcement.section} className="fs-7" />
                <span className="text-muted fs-7">{formatAnnouncementDate(announcement.published_on)}</span>
            </div>
            <h2 className="fw-bold mb-2" style={{ color: '#1e2a4a' }}>{announcement.title}</h2>
            <div className="d-flex align-items-center gap-2 text-gray-600 fs-7 mb-6 pb-5 border-bottom">
                <i className="bi bi-person-circle fs-5"></i>
                <span className="fw-semibold">{announcement.author}</span>
                <span className="text-gray-400">·</span>
                <span>Publicado {formatAnnouncementDateTime(announcement.created_at)}</span>
            </div>
            <div className="announcements-content" dangerouslySetInnerHTML={{ __html: announcement.content || '' }} />
            {announcement.images?.length > 0 && (
                <div className="mt-8">
                    <div className="text-muted fs-7 fw-semibold mb-3">
                        <i className="bi bi-images me-1"></i> Imágenes ({announcement.images.length})
                    </div>
                    <div className="d-flex flex-wrap gap-3">
                        {announcement.images.map((img, index) => (
                            <img
                                key={img.id}
                                src={img.url}
                                alt={`Imagen ${index + 1}`}
                                className="announcements-thumb"
                                onClick={() => openImage(index)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

const Inbox = ({ announcements, sections, filters, selected, unreadTotal }) => {
    const { flash } = usePage().props
    const [search, setSearch] = useState(filters.q || '')
    const firstRender = useRef(true)

    const visit = (next, url = route('announcements.index')) => {
        Inertia.get(url, cleanFilters(next), { preserveState: true, preserveScroll: true, replace: true })
    }

    const applyFilters = (changes) => visit({ ...filters, ...changes })

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false
            return
        }
        const timer = setTimeout(() => {
            if (search !== (filters.q || '')) applyFilters({ q: search })
        }, 450)
        return () => clearTimeout(timer)
    }, [search])

    const openAnnouncement = (id) => {
        const page = announcements.current_page > 1 ? announcements.current_page : ''
        Inertia.get(route('announcements.show', id), cleanFilters({ ...filters, page }), {
            preserveState: true,
            preserveScroll: true,
        })
    }

    const clearAll = () => {
        setSearch('')
        visit({})
    }

    const hasFilters = Boolean(filters.q || filters.section || filters.from || filters.to || filters.status)
    const items = announcements.data || []

    return (
        <Row>
            {flash?.success && <AlertDismissible message={flash.success} type="success" />}
            {flash?.error && <AlertDismissible message={flash.error} type="danger" />}
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath="admin/comunicados" />
            </span>

            <Card className="card-flush shadow-sm mb-10 announcements-inbox">
                <Card.Header className="pt-7 flex-wrap gap-4">
                    <Card.Title className="flex-column align-items-start">
                        <span className="fw-bold text-gray-800">Comunicados</span>
                        <span className="text-muted fs-7 fw-semibold mt-1">
                            {unreadTotal > 0
                                ? `Tienes ${unreadTotal} ${unreadTotal === 1 ? 'comunicado nuevo' : 'comunicados nuevos'}`
                                : 'Estás al día'}
                        </span>
                    </Card.Title>
                    <div className="card-toolbar d-flex flex-wrap align-items-center gap-3">
                        <div className="position-relative">
                            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-4 text-gray-500"></i>
                            <Form.Control
                                type="search"
                                size="sm"
                                className="form-control-solid ps-11"
                                placeholder="Buscar por palabra clave…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{ minWidth: 240 }}
                            />
                        </div>
                        <div style={{ width: 140 }}>
                            <DatePicker
                                className="form-control form-control-sm form-control-solid"
                                locale="es"
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Desde"
                                selected={fromYmd(filters.from)}
                                maxDate={fromYmd(filters.to) || new Date()}
                                onChange={(date) => applyFilters({ from: toYmd(date) })}
                                isClearable
                            />
                        </div>
                        <div style={{ width: 140 }}>
                            <DatePicker
                                className="form-control form-control-sm form-control-solid"
                                locale="es"
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Hasta"
                                selected={fromYmd(filters.to)}
                                minDate={fromYmd(filters.from)}
                                maxDate={new Date()}
                                onChange={(date) => applyFilters({ to: toYmd(date) })}
                                isClearable
                            />
                        </div>
                        <ButtonGroup size="sm">
                            <Button
                                variant={filters.status === 'unread' ? 'light' : 'primary'}
                                onClick={() => applyFilters({ status: '' })}
                            >
                                Todos
                            </Button>
                            <Button
                                variant={filters.status === 'unread' ? 'primary' : 'light'}
                                onClick={() => applyFilters({ status: 'unread' })}
                            >
                                No leídos
                            </Button>
                        </ButtonGroup>
                        {hasFilters && (
                            <Button size="sm" variant="light" onClick={clearAll}>
                                Limpiar
                            </Button>
                        )}
                    </div>
                </Card.Header>

                <Card.Body className="pt-2 pb-0 px-0">
                    <div className="d-flex flex-wrap gap-2 px-8 pb-5 border-bottom">
                        <button
                            type="button"
                            className={`announcements-chip ${!filters.section ? 'is-active' : ''}`}
                            onClick={() => applyFilters({ section: '' })}
                        >
                            Todas las secciones
                        </button>
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                type="button"
                                className={`announcements-chip ${String(filters.section) === String(section.id) ? 'is-active' : ''}`}
                                onClick={() => applyFilters({ section: section.id })}
                            >
                                <span className="announcements-chip__dot" style={{ backgroundColor: section.color }}></span>
                                {section.name}
                                {section.unread_count > 0 && (
                                    <span className="announcements-chip__count">{section.unread_count}</span>
                                )}
                            </button>
                        ))}
                    </div>

                    <Row className="g-0">
                        <Col lg={5} className={selected ? 'd-none d-lg-block' : ''}>
                            <div className="announcements-list">
                                {items.length === 0 ? (
                                    <div className="text-center text-muted py-20 px-6">
                                        <i className="bi bi-inbox fs-3x d-block mb-3 text-gray-400"></i>
                                        {hasFilters ? 'No hay comunicados con esos filtros.' : 'Aún no hay comunicados.'}
                                    </div>
                                ) : (
                                    items.map((item) => (
                                        <div
                                            key={item.id}
                                            role="button"
                                            tabIndex={0}
                                            className={[
                                                'announcements-item',
                                                item.is_read ? '' : 'is-unread',
                                                selected?.id === item.id ? 'is-selected' : '',
                                            ].join(' ')}
                                            onClick={() => openAnnouncement(item.id)}
                                            onKeyDown={(e) => e.key === 'Enter' && openAnnouncement(item.id)}
                                        >
                                            <span
                                                className="announcements-item__bar"
                                                style={{ backgroundColor: item.section?.color || '#e1e3ea' }}
                                            ></span>
                                            <div className="flex-grow-1 min-w-0">
                                                <div className="d-flex align-items-center justify-content-between gap-3 mb-1">
                                                    <span className="text-gray-600 fs-7 text-truncate">{item.author}</span>
                                                    <span className="text-muted fs-8 text-nowrap">
                                                        {formatAnnouncementDate(item.published_on)}
                                                    </span>
                                                </div>
                                                <div className="d-flex align-items-start gap-2">
                                                    {!item.is_read && <span className="announcements-item__dot" title="No leído"></span>}
                                                    <div className="min-w-0 flex-grow-1">
                                                        <div className="announcements-item__title">{item.title}</div>
                                                        <div className="announcements-item__excerpt">{item.excerpt}</div>
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-3 mt-2">
                                                    <SectionBadge section={item.section} className="fs-8" />
                                                    {item.images_count > 0 && (
                                                        <span className="text-muted fs-8">
                                                            <i className="bi bi-paperclip"></i> {item.images_count}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="px-6 pt-4">
                                <PaginationList links={announcements.links} />
                            </div>
                        </Col>
                        <Col lg={7} className={selected ? '' : 'd-none d-lg-block'}>
                            <Reader announcement={selected} onBack={() => visit(filters)} />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </Row>
    )
}

Inbox.layout = (page) => <Layout title="Comunicados">{page}</Layout>

export default Inbox
